import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import GameCard from '@/components/feature/GameCard';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { games, categories } from '@/mocks/games';

function renderMarkdown(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="font-heading text-2xl font-bold text-foreground-900 mt-10 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={key++} className="font-semibold text-foreground-900 mt-4 mb-2">
          {line.replace(/\*\*/g, '')}
        </p>
      );
    } else if (line.startsWith('- ')) {
      elements.push(
        <li key={key++} className="text-foreground-600 leading-relaxed ml-4 mb-1 list-disc">
          {line.replace('- ', '')}
        </li>
      );
    } else if (line.startsWith('1. ') || line.match(/^\d+\. /)) {
      elements.push(
        <li key={key++} className="text-foreground-600 leading-relaxed ml-4 mb-1 list-decimal">
          {line.replace(/^\d+\.\s*/, '')}
        </li>
      );
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={key++} className="border-l-4 border-primary-400 pl-4 py-2 my-4 bg-primary-50/50 rounded-r-lg">
          <p className="text-foreground-600 italic">{line.replace('> ', '')}</p>
        </blockquote>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={key++} className="h-3"></div>);
    } else {
      elements.push(
        <p key={key++} className="text-foreground-600 leading-relaxed mb-2">
          {line}
        </p>
      );
    }
    i++;
  }

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles.filter((a) => a.id !== article.id).slice(0, 3);
  }, [article]);

  const relatedGames = useMemo(() => {
    if (!article) return [];
    const catGames = games.filter((g) => g.category === article.category);
    return catGames.slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
          <p className="text-foreground-500 text-lg">文章未找到</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: categoryInfo?.name || article.category, to: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '黄游免费下载',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Article Header */}
            <header className="mb-8">
              {categoryInfo && (
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4 hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {categoryInfo.name}
                </Link>
              )}
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  阅读 12.8k
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="rounded-2xl overflow-hidden mb-10 aspect-[2/1]">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 黄游免费下载`}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="prose-custom max-w-none mb-16">
              {renderMarkdown(article.content)}
            </div>

            {/* Download CTA */}
            <div className="bg-primary-50 rounded-2xl p-8 mb-16 text-center border border-primary-200/50">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
                <i className="ri-download-cloud-2-line text-primary-600 text-2xl"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">
                开始下载体验
              </h3>
              <p className="text-foreground-500 text-sm mb-6 max-w-md mx-auto">
                前往游戏分类页面，找到您感兴趣的游戏，一键免费下载，安全快速
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to={`/category/${article.category}`}
                  className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-gamepad-line mr-2"></i>
                  浏览{categoryInfo?.name || '相关'}游戏
                </Link>
                <Link
                  to="/category/rpg"
                  className="px-7 py-3 rounded-full border border-primary-300 text-primary-600 font-semibold text-sm hover:bg-primary-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line mr-2"></i>
                  查看全部资源
                </Link>
              </div>
            </div>

            {/* Related Games */}
            {relatedGames.length > 0 && (
              <section className="mb-16">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关游戏推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
                  {relatedGames.map((game) => (
                    <GameCard key={game.id} {...game} />
                  ))}
                </div>
              </section>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mb-16">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} {...ra} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}